import { WebRTCShim } from 'janus-gateway-tsdx'
import { IceServer } from '../api/turn'

/** localStorage key that forces ICE to use TURN relay candidates ONLY, for validating TURN.
 * See docs/TURN-RELAY-TESTING.md. Off unless explicitly set to '1' in a browser. */
export const RELAY_ONLY_KEY = 'iris_relay_only'

/** True iff the relay-only debug flag is set. Read per-connection so it can be toggled without a
 * rebuild — set `localStorage.iris_relay_only = '1'` in devtools, place a call, then remove it. */
const relayOnlyEnabled = (): boolean => {
  try {
    return typeof localStorage !== 'undefined' && localStorage.getItem(RELAY_ONLY_KEY) === '1'
  } catch {
    return false
  }
}

/**
 * A drop-in replacement for janus-gateway-tsdx's `WebRTCShim` that injects our TURN `iceServers`
 * into every `RTCPeerConnection` the library creates (ticket D2).
 *
 * The library builds the PeerConnection via `webRTC.newRTCPeerConnection(config)` with a hardcoded
 * default (Google STUN). Since we supply the shim, overriding that one method lets us add TURN
 * relay candidates without forking the library. On UDP-blocked networks (hospital/corporate Wi-Fi)
 * this is what lets media reach Janus at all.
 *
 * The other two shim methods delegate to the stock `WebRTCShim`.
 */
export default class TurnWebRTCShim {
  private readonly base = new WebRTCShim()
  private readonly iceServers: IceServer[]

  constructor(iceServers: IceServer[]) {
    this.iceServers = iceServers
  }

  newRTCPeerConnection = (config: RTCConfiguration): RTCPeerConnection => {
    // Prepend our TURN/STUN entries; keep any the library already set (its default STUN).
    const merged: RTCConfiguration = {
      ...config,
      iceServers: [...this.iceServers, ...(config?.iceServers ?? [])],
    }
    // Debug/validation: force relay-only so a successful call PROVES media goes through TURN
    // (direct + srflx candidates are discarded). Never on unless the localStorage flag is set.
    if (relayOnlyEnabled()) {
      merged.iceTransportPolicy = 'relay'
      // eslint-disable-next-line no-console
      console.warn('[TurnWebRTCShim] iceTransportPolicy=relay (iris_relay_only set) — TURN-only mode')
    }
    return new RTCPeerConnection(merged)
  }

  newRTCSessionDescription = (jsep: RTCSessionDescription): RTCSessionDescription =>
    this.base.newRTCSessionDescription(jsep)

  newRTCIceCandidate = (candidate: RTCIceCandidate): RTCIceCandidate =>
    this.base.newRTCIceCandidate(candidate)
}
